import { useState, useEffect } from 'react';
import { useUrl } from 'crossroad';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lightbulb, Info, Sparkles, AlertTriangle, Settings, ArrowLeft, Save } from 'lucide-react';
import { PersonaSelection } from '@/components/onboarding/persona-selection';
import { ChronologyPreferenceSelection } from '@/components/onboarding/chronology-preference-selection';
import useUser from '@/lib/hooks/use-user';
import { useUserSettings } from '@/lib/hooks/use-user-settings';
import { completeOnboarding } from '@/lib/user-settings';
import { Analytics } from '@/lib/analytics';
import { toast } from 'sonner';
import { ContextHero } from '@/components/context/ContextHero';
import { motion } from 'framer-motion';
import settingsHero from '../assets/clean-hero6.png';
import bgTexture from '../assets/glen-texture.jpg';

const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
};

const staggerContainer = {
    animate: { transition: { staggerChildren: 0.1 } }
};

export default function OnboardingPage() {
    const [, setUrl] = useUrl();
    const { user } = useUser();
    const { settings, refresh, isLoading, needsOnboarding } = useUserSettings(user);
    const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);
    const [preferredChronology, setPreferredChronology] = useState<BiblicalChronology>('Textual');
    const [isSaving, setIsSaving] = useState(false);

    // Sync state when settings load
    useEffect(() => {
        if (settings?.selectedPersonaId) {
            setSelectedPersonaId(settings.selectedPersonaId);
        }
        if (settings?.preferredChronology) {
            setPreferredChronology(settings.preferredChronology);
        }
    }, [settings?.selectedPersonaId, settings?.preferredChronology]);

    const handleConfirm = async () => {
        if (!user || !selectedPersonaId) return;

        setIsSaving(true);
        try {
            await completeOnboarding(user.uid, selectedPersonaId, preferredChronology);
            Analytics.personaSelected(selectedPersonaId, 'manual');

            if (needsOnboarding) {
                Analytics.onboardingComplete(selectedPersonaId);
                sessionStorage.setItem('onboarding-just-completed', 'true');
            }

            toast.success('Settings updated successfully');
            await refresh();

            if (needsOnboarding) {
                setUrl('/app/read');
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-pulse text-muted-foreground">Loading your preferences...</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-full bg-background relative overflow-x-hidden">
            {/* Background Image with Glassy Overlay */}
            <div
                className="fixed inset-0 z-0 bg-cover bg-center saturate-[0.15] opacity-20 dark:opacity-30 pointer-events-none"
                style={{ backgroundImage: `url(${bgTexture})` }}
            />
            <div className="fixed inset-0 z-1 bg-background/60 backdrop-blur-3xl pointer-events-none" />

            <ContextHero
                eyebrow="SYSTEM PREFERENCES"
                headline="Personalize Your Expedition"
                subtitle="Configure your study persona and chronological preferences to tailor the BibleGlen experience to your journey."
                image={settingsHero}
                onClose={() => setUrl('/app/read')}
                showClose={false}
                variant="standard"
                className="rounded-b-none z-10"
            >
                <div className="flex items-center gap-4">
                    <Button
                        variant="secondary"
                        size="sm"
                        className="rounded-full bg-white/20 hover:bg-white/30 text-white border-white/20 backdrop-blur-md"
                        onClick={() => setUrl('/app/read')}
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Reading
                    </Button>
                    <Button
                        size="sm"
                        className="rounded-full shadow-lg"
                        onClick={handleConfirm}
                        disabled={!selectedPersonaId || isSaving}
                    >
                        {isSaving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Preferences</>}
                    </Button>
                </div>
            </ContextHero>

            <main className="flex-grow relative z-10 pb-32">
                <div className="container max-w-6xl mx-auto px-6 py-16">
                    <motion.div
                        variants={staggerContainer}
                        initial="initial"
                        animate="animate"
                        className="space-y-24"
                    >
                        {/* Section 1: Persona */}
                        <motion.section variants={fadeInUp} className="space-y-10">
                            <div className="flex flex-col items-center text-center space-y-4 max-w-2xl mx-auto">
                                <div className="p-3 bg-primary/10 rounded-2xl text-primary backdrop-blur-md border border-primary/20 shadow-inner">
                                    <Lightbulb className="w-8 h-8" />
                                </div>
                                <div className="space-y-2">
                                    <h2 className="text-4xl font-bold tracking-tight">The Explorer's Persona</h2>
                                    <p className="text-lg text-muted-foreground leading-relaxed">Choose the lens through which you'll encounter the text. Your persona shapes the depth of scholarly research and the focus of guided discovery.</p>
                                </div>
                            </div>

                            <div className="p-2 rounded-[2.5rem] bg-muted/5 border border-border/40 backdrop-blur-sm shadow-2xl">
                                <PersonaSelection
                                    selectedPersonaId={selectedPersonaId}
                                    onSelect={setSelectedPersonaId}
                                />
                            </div>
                        </motion.section>

                        {/* Section 2: Chronology */}
                        <motion.section variants={fadeInUp} className="space-y-10">
                            <div className="flex flex-col items-center text-center space-y-4 max-w-2xl mx-auto">
                                <div className="p-3 bg-primary/10 rounded-2xl text-primary backdrop-blur-md border border-primary/20 shadow-inner">
                                    <Settings className="w-8 h-8" />
                                </div>
                                <div className="space-y-2">
                                    <h2 className="text-4xl font-bold tracking-tight">Chronological Anchor</h2>
                                    <p className="text-lg text-muted-foreground leading-relaxed">Anchor your expedition to a historical framework. This determines how dates and events are synchronized across the ancient world.</p>
                                </div>
                            </div>

                            <ChronologyPreferenceSelection
                                value={preferredChronology}
                                onChange={setPreferredChronology}
                            />
                        </motion.section>

                        {/* Section 3: AI Transparency */}
                        <motion.section variants={fadeInUp} className="space-y-12 pt-16 border-t border-border/20">
                            <div className="flex flex-col items-center text-center space-y-4 max-w-3xl mx-auto">
                                <div className="p-3 bg-primary/10 rounded-2xl text-primary backdrop-blur-md border border-primary/20">
                                    <Sparkles className="w-8 h-8" />
                                </div>
                                <div className="space-y-2">
                                    <h2 className="text-3xl font-bold tracking-tight">Guided by Integrity</h2>
                                    <p className="text-muted-foreground text-lg">Our commitment to scholarly accuracy and the ethical use of artificial intelligence in biblical research.</p>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-8">
                                <Card className="border-primary/20 bg-primary/5 dark:bg-primary/10 backdrop-blur-xl shadow-xl rounded-[2rem] overflow-hidden group">
                                    <CardHeader className="p-8 pb-4">
                                        <CardTitle className="text-2xl font-bold flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary">
                                                <Info className="h-6 w-6" />
                                            </div>
                                            Grounded Synthesis
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="p-8 pt-0 space-y-6">
                                        <p className="text-muted-foreground leading-relaxed">
                                            We employ high-fidelity large language models as sophisticated research assistants. They pre-process vast quantities of archaeological reports, historical texts, and literary analyses to surface the scholarly consensus.
                                        </p>
                                        <Button
                                            variant="outline"
                                            size="lg"
                                            className="w-full rounded-full border-primary/30 hover:bg-primary/20 transition-all font-bold tracking-tight"
                                            onClick={() => window.open('/app/field-guide/how-we-use-ai?hideBack=true', '_blank')}
                                        >
                                            View AI Methodology
                                        </Button>
                                    </CardContent>
                                </Card>

                                <Card className="border-yellow-500/20 bg-yellow-500/5 dark:bg-yellow-500/10 backdrop-blur-xl shadow-xl rounded-[2rem] overflow-hidden group">
                                    <CardHeader className="p-8 pb-4">
                                        <CardTitle className="text-2xl font-bold flex items-center gap-3 text-yellow-600 dark:text-yellow-500">
                                            <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                                                <AlertTriangle className="h-6 w-6" />
                                            </div>
                                            Academic Limits
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="p-8 pt-0 space-y-6">
                                        <p className="text-muted-foreground leading-relaxed">
                                            Artificial intelligence is a powerful aid, but not a final authority. While we strive for high academic fidelity, we encourage every explorer to verify findings using the primary sources linked throughout the app.
                                        </p>
                                        <div className="p-4 rounded-2xl bg-yellow-500/5 border border-yellow-500/10 italic text-sm text-yellow-700 dark:text-yellow-400/70">
                                            Always cross-reference critical findings with established commentaries and primary archaeological records.
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </motion.section>

                        {/* Final CTA */}
                        <motion.div variants={fadeInUp} className="flex flex-col items-center gap-6 pt-12">
                            <div className="h-px w-24 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
                            <Button
                                onClick={handleConfirm}
                                disabled={!selectedPersonaId || isSaving}
                                size="xl"
                                className="min-w-[280px] h-16 text-lg rounded-full shadow-[0_0_30px_rgba(var(--primary),0.3)] hover:shadow-[0_0_50px_rgba(var(--primary),0.5)] transition-all bg-primary hover:bg-primary/90 font-bold tracking-tighter"
                            >
                                {isSaving ? 'Synchronizing...' : 'Save Expedition Settings'}
                            </Button>
                            <p className="text-xs text-muted-foreground uppercase tracking-[0.3em] font-bold opacity-60">Synced to Cloud Profile</p>
                        </motion.div>
                    </motion.div>
                </div>
            </main>
        </div>
    );
}

